
if (!customElements.get('discount-progress-bar')) {
	class DiscountProgressBar extends HTMLElement {
		constructor() {
			super();

			this.state = {
				cartTotal: 0,
				currencySymbol: '$',
				goals: [],
				currentGoalIndex: -1,
				previousGoalIndex: -1,
				progressPercentage: 0
			};

			this.elements = {
				progressFill: null,
				progressText: null,
				milestones: []
			};

			this.animationFrameId = null;
			this.updateTimer = null;
		}

		connectedCallback() {
			this.initializeComponent();
			this.setupEventListeners();
			this.updateProgressBar();
		}

		disconnectedCallback() {
			this.cleanup();
		}

		initializeComponent() {

			function toNumber(value) {
				if (typeof value !== 'string') return Number(value) || 0; 
				return Number(value.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '')) || 0;
			} 
			
			const cartTotalAttr = this.getAttribute('data-cart-total'); 
			this.state.cartTotal = cartTotalAttr ? parseFloat(cartTotalAttr) : 0; 
			if (Shopify.currency.active === 'EUR') { 
				this.state.cartTotal = parseFloat(cartTotalAttr) / 100; 
			} else if (Shopify.currency.active === 'DKK') { 
				this.state.cartTotal = parseFloat(cartTotalAttr) / 100; 
			} else if (Shopify.currency.active === 'PLN') { 
				this.state.cartTotal = parseFloat(cartTotalAttr) / 100; 
			} else if (Shopify.currency.active === 'QAR') { 
				this.state.cartTotal = parseFloat(cartTotalAttr) / 100; 
			} else if (Shopify.currency.active === 'RON') { 
				this.state.cartTotal = parseFloat(cartTotalAttr) / 100; 
			} else if (Shopify.currency.active === 'HUF') { 
				this.state.cartTotal = toNumber(cartTotalAttr); 
			} else if (Shopify.currency.active === 'IDR') { 
				this.state.cartTotal = toNumber(cartTotalAttr) / 100; 
			}

			this.state.currencySymbol = this.getAttribute('data-currency-symbol') || '$';
			this.elements.progressFill = this.querySelector('.progress-bar-fill');
			this.elements.progressText = this.querySelector('.progress-text');
			this.elements.milestones = Array.from(this.querySelectorAll('.progress-milestone'));


			this.state.goals = this.elements.milestones
				.map((milestone, index) => {
					const goalAmount = parseFloat(milestone.getAttribute('data-goal')) * parseFloat(window.Shopify.currency.rate) || 0;
					if (goalAmount <= 0) return null;

					return {
						index,
						amount: goalAmount,
						element: milestone,
						title: milestone.querySelector('.milestone-discount')?.textContent || '',
						description: milestone.querySelector('.milestone-label')?.textContent || '',
						messageBefore: this.getGoalMessage(index + 1, 'before'),
						messageReached: this.getGoalMessage(index + 1, 'reached')
					};
				})
				.filter(goal => goal !== null)
				.sort((a, b) => a.amount - b.amount);

			if (this.state.goals.length === 0) {
				console.warn('DiscountProgressBar: No valid goals configured');
			}
		}

		getGoalMessage(goalNumber, type) {
			const script = this.querySelector(`script[data-goal-${goalNumber}-${type}]`);
			return script ? script.textContent.trim() : '';
		}

		setupEventListeners() {
			document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
			document.addEventListener('cart:change', this.handleCartUpdate.bind(this));
			document.addEventListener('variant:change', this.handleCartUpdate.bind(this));
			document.addEventListener('shopify:section:load', this.handleSectionReload.bind(this));
		}

		handleCartUpdate(event) {
			let newTotal = this.state.cartTotal;

			if (event && event.detail && event.detail.cart) {
				newTotal = this.extractCartTotal(event.detail.cart);
			} else {
				this.fetchCartAndUpdate();
				return;
			}
			this.updateCartTotal(newTotal);
		}

		extractCartTotal(cart) {
			if (typeof cart.total_price === 'number') {
				return cart.total_price / 100;
			}
			if (typeof cart.total_price === 'string') {
				return parseFloat(cart.total_price.replace(/[^0-9.]/g, ''));
			}
			return this.state.cartTotal;
		}

		async fetchCartAndUpdate() {
			try {
				const response = await fetch('/cart.js'); 
				const cart = await response.json();
				const newTotal = this.extractCartTotal(cart);
				this.updateCartTotal(newTotal);
			} catch (error) {
				console.error('DiscountProgressBar: Error fetching cart', error);
			}
		}

		updateCartTotal(newTotal) {
			if (this.state.cartTotal === newTotal) return; 
			if (Shopify.currency.active === 'EUR') {
				newTotal = newTotal / 100;
			}
			this.state.cartTotal = newTotal;
			this.setAttribute('data-cart-total', newTotal);

			clearTimeout(this.updateTimer);
			this.updateTimer = setTimeout(() => {
				this.updateProgressBar();
			}, 100);
		}

		updateProgressBar() {
			if (!this.elements.progressFill || this.state.goals.length === 0) return;
			this.calculateProgress();
			this.animateProgressBar();
			this.updateMilestones();
			this.updateMessage();
		}

		calculateProgress() {
			const cartTotal = this.state.cartTotal;
			const goals = this.state.goals;
			const totalGoals = goals.length;

			this.state.previousGoalIndex = this.state.currentGoalIndex;

			if (cartTotal <= 0) {
				this.state.currentGoalIndex = -1;
				this.state.progressPercentage = 0;
				return;
			}

			let completedGoals = 0;

			for (let i = 0; i < totalGoals; i++) {
				if (cartTotal >= goals[i].amount) {
					completedGoals++;
				} else {
					break;
				}
			}

			if (completedGoals === 0) {
				this.state.currentGoalIndex = -1;
				this.state.progressPercentage = 10;
				return;
			}

			const progressPercentage = (completedGoals / totalGoals) * 100;
			this.state.currentGoalIndex = completedGoals - 1;
			this.state.progressPercentage = Math.min(100, progressPercentage);
		}

		animateProgressBar() {
			const progressFill = this.elements.progressFill;
			const percentage = this.state.progressPercentage;
			const goalJustReached = this.state.currentGoalIndex > this.state.previousGoalIndex;
			requestAnimationFrame(() => {
				progressFill.style.width = `${percentage}%`;
				progressFill.setAttribute('data-progress', percentage.toFixed(2));

				if (goalJustReached) {
					progressFill.classList.add('goal-reached');
					setTimeout(() => {
						progressFill.classList.remove('goal-reached');
					}, 600);
				}
			});
		}

		updateMilestones() {
			this.state.goals.forEach((goal, index) => {
				const element = goal.element;

				element.classList.remove('active', 'reached', 'next-goal');

				if (index < this.state.currentGoalIndex) {
					element.classList.add('reached');
				} else if (index === this.state.currentGoalIndex) {
					element.classList.add('active', 'reached');
				} else if (index === this.state.currentGoalIndex + 1) {
					element.classList.add('next-goal');
				}
			});
		}

		updateMessage() {
			if (!this.elements.progressText) return;

			const cartTotal = this.state.cartTotal;
			const nextGoalIndex = this.state.currentGoalIndex + 1;

			let message = '';
			if (this.state.currentGoalIndex >= this.state.goals.length - 1 &&
				cartTotal >= this.state.goals[this.state.goals.length - 1].amount) {
				const lastGoal = this.state.goals[this.state.goals.length - 1];
				message = lastGoal.messageReached || 'Congratulations! You\'ve unlocked all rewards!';
			}
			else if (nextGoalIndex < this.state.goals.length) {
				const nextGoal = this.state.goals[nextGoalIndex];
				const amountNeeded = nextGoal.amount - cartTotal;

				if (amountNeeded > 0) {
					message = nextGoal.messageBefore || '';
					const formattedAmount = `<strong>${this.formatCurrency(amountNeeded)}</strong>`;
					message = message.replace(/\{amount\}/g, formattedAmount);
				} else {
					message = nextGoal.messageReached || '';
				}
			}

			this.updateMessageText(message);
		}

		updateMessageText(newMessage) {
			const textElement = this.elements.progressText;
			const currentMessage = textElement.textContent.trim();
			if (currentMessage === newMessage) return;
			textElement.classList.remove('visible');

			setTimeout(() => {
				textElement.innerHTML = `<span>${newMessage}</span>`;

				requestAnimationFrame(() => {
					textElement.classList.add('visible');
				});
			}, 200);
		}


		formatCurrency(amount) {
			const formatted = amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
			return `${this.state.currencySymbol} ${formatted}`;
		}

		handleSectionReload() {
			this.initializeComponent();
			this.updateProgressBar();
		}

		cleanup() {
			clearTimeout(this.updateTimer);

			if (this.animationFrameId) {
				cancelAnimationFrame(this.animationFrameId);
			}

			document.removeEventListener('cart:updated', this.handleCartUpdate);
			document.removeEventListener('cart:change', this.handleCartUpdate);
			document.removeEventListener('variant:change', this.handleCartUpdate);
			document.removeEventListener('shopify:section:load', this.handleSectionReload);
		}

		setCartTotal(newTotal) {
			this.updateCartTotal(newTotal);
		}

		getState() {
			return { ...this.state };
		}

	}

	customElements.define('discount-progress-bar', DiscountProgressBar);
}